import { useState, useEffect, useRef } from 'react';
import api from '../services/api';

export function useRecipeForm({ id, onSaved, navigate, initialData }) {
  const [loading, setLoading] = useState(false);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const imageInputRef = useRef(null);
  const [stepImageFiles, setStepImageFiles] = useState([null]);
  const [stepImagePreviews, setStepImagePreviews] = useState([null]);
  const stepImageInputRefs = useRef([]);
  const dragSrcIdx = useRef(null);
  const canDrag = useRef(false);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const prepTimeRef = useRef(null);
  const cookTimeRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    difficulty: '',
    servings: 4,
    prep_time: 0,
    cook_time: 0,
    ingredients: [{ amount: '', unit: '', name: '' }],
    steps: [{ text: '', image_path: null }],
    tags: [],
    is_public: false,
  });

  const originalOcr = useRef(null);
  const isScanned = useRef(false);

  useEffect(() => {
    setRightsConfirmed(false);
    if (id) loadRecipe();
    else if (initialData) applyRecipeData(initialData);
  }, [id]); // eslint-disable-line

  function applyRecipeData(r) {
    const loadedSteps = r.steps?.length
      ? r.steps.map(s => typeof s === 'string' ? { text: s, image_path: null } : s)
      : [{ text: '', image_path: null }];
    setFormData({
      title:       r.title       || '',
      description: r.description || '',
      category:    r.category    || '',
      difficulty:  r.difficulty  || '',
      servings:    r.servings    || 4,
      prep_time:   r.prep_time   || 0,
      cook_time:   r.cook_time   || 0,
      ingredients: r.ingredients?.length ? r.ingredients : [{ amount: '', unit: '', name: '' }],
      steps:       loadedSteps,
      tags:        r.tags        || [],
      is_public:   !!r.is_public,
    });
    if (r.image_url || r.image_path) setImagePreview(r.image_url || r.image_path);
    setStepImageFiles(loadedSteps.map(() => null));
    setStepImagePreviews(loadedSteps.map(s => s.image_path || null));
    if (r.source_file && r.structured?.ingredients?.length) {
      isScanned.current   = true;
      originalOcr.current = {
        title:       r.structured.title || r.title,
        ingredients: r.structured.ingredients,
        steps:       r.structured.steps || [],
      };
    }
  }

  async function loadRecipe() {
    try {
      const data = await api.getRecipe(id);
      applyRecipeData(data.recipe);
    } catch (error) {
      console.error('Failed to load recipe:', error);
    }
  }

  function buildCorrections(form) {
    if (!isScanned.current || !originalOcr.current) return [];
    const orig  = originalOcr.current;
    const corrs = [];
    const origTitle = String(orig.title || '').trim();
    const newTitle  = String(form.title  || '').trim();
    if (origTitle && newTitle && origTitle !== newTitle)
      corrs.push({ field: 'title', field_index: null, original: origTitle, corrected: newTitle });
    const len = Math.min(orig.ingredients.length, form.ingredients.length);
    for (let i = 0; i < len; i++) {
      const o = orig.ingredients[i] || {};
      const n = form.ingredients[i] || {};
      for (const f of ['amount', 'unit', 'name']) {
        const ov = String(o[f] ?? '').trim();
        const nv = String(n[f] ?? '').trim();
        if (ov && nv && ov !== nv)
          corrs.push({ field: `ingredient_${f}`, field_index: i, original: ov, corrected: nv });
      }
    }
    const sLen = Math.min(orig.steps.length, form.steps.length);
    for (let i = 0; i < sLen; i++) {
      const ov = String(orig.steps[i] || '').trim();
      const nv = String((form.steps[i]?.text ?? form.steps[i]) || '').trim();
      if (ov && nv && ov !== nv)
        corrs.push({ field: 'step', field_index: i, original: ov, corrected: nv });
    }
    return corrs;
  }

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  function stepServings(delta) {
    setFormData(prev => ({
      ...prev,
      servings: Math.max(1, Math.min(99, (prev.servings || 1) + delta))
    }));
  }

  function handleIngredientChange(index, field, value) {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setFormData(prev => ({ ...prev, ingredients: newIngredients }));
  }

  function addIngredient() {
    setFormData(prev => ({ ...prev, ingredients: [...prev.ingredients, { amount: '', unit: '', name: '' }] }));
  }

  function removeIngredient(index) {
    setFormData(prev => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== index) }));
  }

  function handleStepChange(index, value) {
    const newSteps = [...formData.steps];
    newSteps[index] = { ...(newSteps[index] || {}), text: value };
    setFormData(prev => ({ ...prev, steps: newSteps }));
  }

  function handleStepImageChange(index, file) {
    if (!file) return;
    const newFiles = [...stepImageFiles];
    const newPreviews = [...stepImagePreviews];
    newFiles[index] = file;
    newPreviews[index] = URL.createObjectURL(file);
    setStepImageFiles(newFiles);
    setStepImagePreviews(newPreviews);
  }

  function removeStepImage(index) {
    const newFiles = [...stepImageFiles];
    const newPreviews = [...stepImagePreviews];
    const newSteps = [...formData.steps];
    newFiles[index] = null;
    newPreviews[index] = null;
    newSteps[index] = { ...(newSteps[index] || {}), image_path: null };
    setStepImageFiles(newFiles);
    setStepImagePreviews(newPreviews);
    setFormData(prev => ({ ...prev, steps: newSteps }));
  }

  function handleStepImagesChange(index, images) {
    setFormData(prev => {
      const newSteps = [...prev.steps];
      newSteps[index] = { ...(newSteps[index] || {}), images };
      return { ...prev, steps: newSteps };
    });
  }

  function addStep() {
    setFormData(prev => ({ ...prev, steps: [...prev.steps, { text: '', image_path: null, images: [] }] }));
    setStepImageFiles(prev => [...prev, null]);
    setStepImagePreviews(prev => [...prev, null]);
  }

  function removeStep(index) {
    setFormData(prev => ({ ...prev, steps: prev.steps.filter((_, i) => i !== index) }));
    setStepImageFiles(prev => prev.filter((_, i) => i !== index));
    setStepImagePreviews(prev => prev.filter((_, i) => i !== index));
  }

  function handleDragStart(e, idx) {
    if (!canDrag.current) { e.preventDefault(); return; }
    dragSrcIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e, idx) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIdx(idx);
  }

  function handleDrop(e, idx) {
    e.preventDefault();
    const src = dragSrcIdx.current;
    if (src === null || src === idx) { setDragOverIdx(null); return; }
    const reorder = (arr) => {
      const next = [...arr];
      const [item] = next.splice(src, 1);
      next.splice(idx, 0, item);
      return next;
    };
    setFormData(prev => ({ ...prev, steps: reorder(prev.steps) }));
    setStepImageFiles(prev => reorder(prev));
    setStepImagePreviews(prev => reorder(prev));
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  }

  function handleDragEnd() {
    canDrag.current = false;
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  }

  function addTag(raw) {
    const t = raw.trim().toLowerCase();
    if (t && !formData.tags.includes(t))
      setFormData(prev => ({ ...prev, tags: [...prev.tags, t] }));
    setTagInput('');
  }

  function removeTag(tag) {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function handleCoverCameraCapture(file) {
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function handleImageRemove() {
    setImageFile(null);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errors = {};
    if (!formData.prep_time || formData.prep_time <= 0) errors.prep_time = 'Bitte Vorbereitungszeit angeben';
    if (!formData.cook_time || formData.cook_time <= 0) errors.cook_time = 'Bitte Zubereitungszeit angeben';
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      if (errors.prep_time) prepTimeRef.current?.focus();
      else if (errors.cook_time) cookTimeRef.current?.focus();
      return;
    }
    setValidationErrors({});
    setLoading(true);
    try {
      let savedId;
      if (id) {
        await api.updateRecipe(id, formData);
        const corrs = buildCorrections(formData);
        if (corrs.length > 0) api.saveCorrections(parseInt(id), corrs).catch(() => {});
        savedId = id;
      } else {
        const result = await api.createRecipe(formData);
        savedId = result.recipe.id;
      }

      if (imageFile) {
        try {
          await api.uploadRecipeCover(savedId, imageFile);
        } catch (err) {
          setSaveError('Titelbild abgelehnt: ' + err.message + ' Bitte wähle ein anderes Bild.');
          setLoading(false);
          if (!id) navigate(`/edit/${savedId}`, { replace: true });
          return;
        }
      }

      for (let i = 0; i < stepImageFiles.length; i++) {
        if (stepImageFiles[i]) {
          try {
            await api.uploadStepImage(savedId, i, stepImageFiles[i]);
          } catch (err) {
            setSaveError(`Bild für Schritt ${i + 1} abgelehnt: ${err.message} Bitte wähle ein anderes Bild.`);
            setLoading(false);
            if (!id) navigate(`/edit/${savedId}`, { replace: true });
            return;
          }
        }
      }

      if (onSaved) onSaved({ id: savedId, ...formData });
      else navigate(`/recipe/${savedId}`, { state: { from: 'editor' } });
    } catch (error) {
      setSaveError('Speichern abgelehnt: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  return {
    formData,
    loading,
    rightsConfirmed, setRightsConfirmed,
    tagInput, setTagInput,
    imageFile, imagePreview, imageInputRef,
    stepImageFiles, stepImagePreviews, stepImageInputRefs,
    dragOverIdx, canDrag,
    validationErrors, setValidationErrors,
    saveError,
    showDeleteConfirm, setShowDeleteConfirm,
    prepTimeRef, cookTimeRef,
    handleChange, stepServings,
    handleIngredientChange, addIngredient, removeIngredient,
    handleStepChange, handleStepImageChange, removeStepImage, addStep, removeStep,
    handleStepImagesChange,
    handleDragStart, handleDragOver, handleDrop, handleDragEnd,
    addTag, removeTag,
    handleImageChange,
    handleImageRemove,
    handleCoverCameraCapture,
    handleSubmit,
  };
}
